import { useMemo, useState } from 'react';
import { TrendingUp, AlertCircle } from 'lucide-react';
import useLocalState from '../hooks/useLocalState.js';
import { simulateMultiHorizon } from '../lib/dca.js';
import {
  modelAgeYears,
  impliedAnnualGrowthRate,
  powerLawFairValue,
  POWER_LAW_MODEL,
} from '../lib/powerLaw.js';
import { formatUsd, formatBtc, formatPct } from '../lib/format.js';

const FREQUENCY_OPTIONS = [
  { value: 'weekly',   label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly',  label: 'Monthly' },
];

const HORIZONS = [1, 5, 10];

function isoDateLocal(d) {
  // Format a Date as YYYY-MM-DD using LOCAL time so the <input type=date>
  // displays the user's actual day. Using toISOString() would shift the
  // displayed value across timezones (a known FRED-date pitfall flagged
  // in the project's CLAUDE.md).
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function parseLocalDate(s) {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function CardShell({ children }) {
  return (
    <div className="rounded-2xl bg-stone-900/60 border border-stone-800 p-6 hover:border-amber-500/40 transition-colors">
      {children}
    </div>
  );
}

export default function DcaSimulator() {
  // Persist the user's draft in localStorage so they don't lose it on
  // refresh. Defaults to a "$100/month starting today" baseline.
  const [config, setConfig] = useLocalState(
    'dcaConfig',
    {
      amount: 100,
      frequency: 'monthly',
      startDateIso: isoDateLocal(new Date()),
    },
    1,
  );

  const [draftAmount, setDraftAmount] = useState(String(config.amount || ''));

  const startDate = useMemo(
    () => parseLocalDate(config.startDateIso) ?? new Date(),
    [config.startDateIso],
  );

  const projections = useMemo(() => {
    const amt = parseFloat(draftAmount);
    if (!Number.isFinite(amt) || amt <= 0) return null;
    return simulateMultiHorizon(
      { amountPerPeriod: amt, frequency: config.frequency, startDate },
      HORIZONS,
    );
  }, [draftAmount, config.frequency, startDate]);

  // Persist the parsed amount on input commit (blur) so the localStorage
  // value matches what the user actually intends, not every keystroke.
  function commitAmount() {
    const parsed = parseFloat(draftAmount);
    const clean = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    setConfig(prev => ({ ...prev, amount: clean }));
    setDraftAmount(String(clean));
  }

  return (
    <CardShell>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-400 flex items-center justify-center">
          <TrendingUp size={20} className="text-stone-900" />
        </div>
        <h3 className="font-display text-lg font-semibold text-cream">DCA Simulator</h3>
      </div>

      <p className="text-stone-400 text-sm leading-relaxed mb-4">
        Project where consistent saving lands you against the bitcoin power-law band.
      </p>

      {/* Inputs */}
      <div className="space-y-3 mb-5">
        <div>
          <label className="block text-xs uppercase tracking-wider text-stone-500 mb-1">
            Amount per period (USD)
          </label>
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="1"
            value={draftAmount}
            onChange={e => setDraftAmount(e.target.value)}
            onBlur={commitAmount}
            placeholder="100"
            className="w-full bg-stone-950/60 border border-stone-700 rounded-lg px-3 py-2 text-cream font-mono tabular-nums focus:outline-none focus:border-amber-500/60"
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs uppercase tracking-wider text-stone-500 mb-1">
              Frequency
            </label>
            <select
              value={config.frequency}
              onChange={e => setConfig(prev => ({ ...prev, frequency: e.target.value }))}
              className="w-full bg-stone-950/60 border border-stone-700 rounded-lg px-3 py-2 text-cream text-sm focus:outline-none focus:border-amber-500/60"
            >
              {FREQUENCY_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-stone-500 mb-1">
              Starting
            </label>
            <input
              type="date"
              value={config.startDateIso}
              onChange={e => setConfig(prev => ({ ...prev, startDateIso: e.target.value }))}
              className="w-full bg-stone-950/60 border border-stone-700 rounded-lg px-3 py-2 text-cream text-sm focus:outline-none focus:border-amber-500/60"
            />
          </div>
        </div>
      </div>

      {/* Horizon results */}
      {projections ? (
        <div className="space-y-2">
          {projections.map(({ horizonYears, result }) => (
            <HorizonRow key={horizonYears} years={horizonYears} result={result} />
          ))}
          <ModelExplainer />
        </div>
      ) : (
        <div className="rounded-lg bg-stone-950/40 p-4 text-sm text-stone-500 flex items-start gap-2">
          <AlertCircle size={14} className="text-stone-500 flex-shrink-0 mt-0.5" />
          <span>Enter a positive amount to see your projection.</span>
        </div>
      )}
    </CardShell>
  );
}

function HorizonRow({ years, result }) {
  if (!result) {
    return (
      <div className="rounded-lg bg-stone-950/40 p-3">
        <div className="flex items-baseline justify-between mb-1">
          <span className="text-xs uppercase tracking-wider text-stone-500">{years}-year</span>
          <span className="text-xs text-stone-600">—</span>
        </div>
      </div>
    );
  }
  const { totalContributed, band, horizonDate } = result;
  const horizonYearLabel = horizonDate.getFullYear();
  return (
    <div className="rounded-lg bg-stone-950/40 p-4">
      <div className="flex items-baseline justify-between mb-3">
        <span className="text-xs uppercase tracking-wider text-stone-500">
          {years}-year ({horizonYearLabel})
        </span>
        <span className="text-xs text-stone-600 font-mono tabular-nums">
          {formatUsd(totalContributed)} saved in
        </span>
      </div>

      {/* Headline: the "likely" outcome */}
      <div className="mb-2">
        <div className="text-[10px] uppercase tracking-wider text-amber-400/80 mb-0.5">
          Likely worth
        </div>
        <div className="flex items-baseline gap-2">
          <span className="font-mono tabular-nums text-2xl font-bold text-cream">
            {formatUsd(band.fair.usd)}
          </span>
          <span className="font-mono tabular-nums text-xs text-stone-500">
            {formatBtc(band.fair.btc)}
          </span>
        </div>
      </div>

      {/* Range: low/high outcomes from the band */}
      <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-stone-800/60">
        <RangeBucket
          label="If price stays high"
          tone="rose"
          usd={band.ceiling.usd}
          btc={band.ceiling.btc}
          help="few cheap dips — each $ buys less BTC"
        />
        <RangeBucket
          label="If price dips deep"
          tone="emerald"
          usd={band.floor.usd}
          btc={band.floor.btc}
          help="real bear markets — each $ buys more BTC"
        />
      </div>
    </div>
  );
}

function ModelExplainer() {
  // Compute on render — pure functions of "now", and the card re-renders
  // often enough (every keystroke / refetch) that staleness doesn't matter.
  const now = new Date();
  const yearsOfData = modelAgeYears(now);
  const fairToday = powerLawFairValue(now);
  const growthNow = impliedAnnualGrowthRate(now, 0);
  const growth10y = impliedAnnualGrowthRate(now, 10);
  const beta = POWER_LAW_MODEL.fair.exp;

  return (
    <details className="mt-3 rounded-lg bg-stone-950/40 p-3 group">
      <summary className="cursor-pointer text-[11px] text-stone-400 font-semibold list-none flex items-center gap-1.5">
        <span className="text-amber-400/80 group-open:rotate-90 transition-transform inline-block">›</span>
        How to read this — and what "long-run trend" actually means
      </summary>

      <div className="text-[11px] text-stone-500 leading-relaxed space-y-2 pt-2 pl-3 border-l border-stone-800">
        <p>
          The big <span className="text-amber-400/90">"Likely worth"</span> number uses the
          bitcoin power-law model — a curve fit to{' '}
          <span className="text-stone-300 font-mono">{yearsOfData.toFixed(1)} years</span> of
          actual price data since the genesis block (Jan 3, 2009). The model says price
          tracks <span className="text-stone-300 font-mono">days^{beta}</span> over the long
          run, with bull and bear cycles oscillating around it.
        </p>
        <p>
          Today the model's fair value is{' '}
          <span className="text-stone-300 font-mono tabular-nums">{formatUsd(fairToday)}</span>{' '}
          per BTC. Its implied growth rate{' '}
          <span className="text-stone-400">decelerates over time</span> — the model itself
          says the next 12 months grow about{' '}
          <span className="text-stone-300 font-mono">{formatPct(growthNow * 100, { decimals: 0 })}</span>
          , dropping to about{' '}
          <span className="text-stone-300 font-mono">{formatPct(growth10y * 100, { decimals: 0 })}</span>{' '}
          ten years from now.
        </p>
        <p>
          The "<span className="text-emerald-400/90">price dips deep</span> /{' '}
          <span className="text-rose-400/80">price stays high</span>" range shows what
          happens if your DCA buys land in cheaper or pricier years than the trend line.
          More bear → more BTC accumulated → bigger stack at horizon.
        </p>
        <p className="text-stone-600">
          The power law is a curve fit, not a law of physics. Regulatory shocks, macro
          regime changes, or a fundamental break in bitcoin's adoption curve could all
          invalidate it. Educational reference — not a forecast.
        </p>
      </div>
    </details>
  );
}

function RangeBucket({ label, tone, btc, usd, help }) {
  const toneClass = {
    emerald: 'text-emerald-400/90',
    rose:    'text-rose-400/80',
  }[tone] || 'text-cream';
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-stone-600 mb-1">{label}</div>
      <div className={`font-mono tabular-nums text-sm font-semibold ${toneClass}`}>
        {formatUsd(usd)}
      </div>
      <div className="font-mono tabular-nums text-[10px] text-stone-500">
        {formatBtc(btc)}
      </div>
      <div className="text-[10px] text-stone-600 leading-tight mt-0.5">{help}</div>
    </div>
  );
}
